"use client";

import React, { useState, useEffect, startTransition } from "react";
import Image from "next/image";
import type { EventData } from "@/types/event";
import type { CouponWithStoreData } from "@/types/couponsWithStoresData";
import type { BlogData } from "@/types/blog";
import EventShow from "@/components/EventShow";
import BlogSection from "@/components/BlogSection";
import CouponModal from "@/components/coupon_popup";
import CouponRevealButton from "@/components/CouponRevealButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useActionState } from "react";
import { createSubscriberAction } from "@/actions/subscriberActions";
import { incrementCouponUsesAction } from "@/actions/couponActions";
import { openAffiliateLink } from "@/lib/openAffiliateLink";
import { getStoreLogoAlt } from "@/lib/storeLogo";

interface FieldErrors {
  [key: string]: string[];
}

interface EventClientProps {
  event: EventData;
  topEvents: EventData[];
  latestEvents: EventData[];
  topDeals: CouponWithStoreData[];
  blogs: BlogData[];
  couponId?: string;
  slug?: string;
  domain?: string | undefined;
}

const EventClient: React.FC<EventClientProps> = ({
  event,
  topEvents,
  topDeals,
  latestEvents,
  blogs,
  couponId,
  slug,
  domain,
}) => {
  const [selectedDeal, setSelectedDeal] = useState<CouponWithStoreData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  // Newsletter states
  const [email, setEmail] = useState("");
  const [formState, dispatch, isPending] = useActionState(createSubscriberAction, {});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const errorFor = (field: string) => {
    return formState.error &&
      typeof formState.error === "object" &&
      field in formState.error
      ? (formState.error as FieldErrors)[field]?.[0]
      : null;
  };

  useEffect(() => {
    if (formState.data && !formState.error) {
      setDialogMessage("You have been successfully subscribed!");
      setDialogOpen(true);
      setEmail("");
    }

    if (formState.error && "message" in formState.error) {
      setDialogMessage((formState.error as any).message?.[0] || "Something went wrong!");
      setDialogOpen(true);
    }
  }, [formState]);

  // Auto-open modal if couponId prop matches a deal
  useEffect(() => {
    if (couponId) {
      const deal = topDeals.find((d) => d._id === couponId);
      if (deal) {
        setSelectedDeal(deal);
        setIsModalOpen(true);
      }
    }
  }, [couponId, topDeals]);

  // Lock background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  const handleGetDealClick = async (deal: CouponWithStoreData) => {
    if (deal.couponUrl) {
      openAffiliateLink(deal.couponUrl, { couponId: deal._id });
    } else if (deal.store?.slug && typeof window !== "undefined") {
      window.location.assign(`/stores/${deal.store.slug}`);
      return;
    }
    try {
      await incrementCouponUsesAction(deal._id);
      setSelectedDeal(deal);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to increment coupon uses:", error);
    }
  };

  const toggleDescription = (dealId: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  };

  const isDescriptionLong = (description: string | undefined): boolean => {
    if (!description) return false;
    return description.length > 100;
  };

  return (
    <div className="w-full flex flex-col items-center bg-gray-50">
      {/* Main content + sidebar */}
      <div className="max-w-7xl w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Event Content */}
        <div className="lg:col-span-9 bg-white rounded-xl shadow border border-gray-100">
          <EventShow event={event} />
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-6">
          {/* Top Events */}
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
            <h3 className="font-semibold text-lg mb-3 text-gray-800">Top Events</h3>
            <ul className="space-y-2">
              {topEvents.map((item) => (
                <li key={item._id} className="flex items-center space-x-2">
                  {item.image && (
                    <Image
                      src={`${domain}${item.image}`}
                      alt={item.title}
                      width={40}
                      height={40}
                      className="rounded object-cover"
                    />
                  )}
                  <a
                    href={`/events/${item.slug}`}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Top Deals */}
          <div className="space-y-4">
            {topDeals.length > 0 ? (
              topDeals.map((deal) => {
              const storeName = deal.store?.name || "Unknown Store";
              const storeInitial = storeName.charAt(0).toUpperCase();
              const usedTimes = deal.uses ?? 0;

              return (
                <div key={deal._id} className="flex flex-col bg-white rounded-xl shadow-md p-4 border border-gray-200 overflow-hidden">
                  <div className="flex items-center mb-3">
                    {deal.store?.image ? (
                      <Image
                        src={`https://www.itscoupons.com${deal.store.image}`}
                        width={40}
                        height={40}
                        alt={getStoreLogoAlt(deal.store.slug || storeName)}
                        className="rounded-md object-cover"
                      />
                    ) : (
                      <div className="bg-purple-700 text-white font-bold flex items-center justify-center rounded-md w-10 h-10">
                        {storeInitial}
                      </div>
                    )}
                    <a
                      href={`/stores/${deal.store?.slug || ""}`}
                      className="ml-2 text-sm font-medium text-purple-700 hover:underline"
                    >
                      {storeName} Coupons
                    </a>
                  </div>

                  <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2">{deal.title}</h4>
                  <div className="mb-3">
                    <p className={`text-xs text-gray-600 leading-relaxed ${expandedDescriptions.has(deal._id) ? '' : 'line-clamp-2'}`}>
                      {deal.description}
                    </p>
                    {isDescriptionLong(deal.description) && (
                      <button
                        onClick={() => toggleDescription(deal._id)}
                        className="text-xs text-purple-700 hover:text-purple-800 font-medium mt-1 hover:underline"
                      >
                        {expandedDescriptions.has(deal._id) ? 'Hide description -' : 'See all description +'}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 mt-auto">
                    {deal.couponType === "coupon" ? (
                      <CouponRevealButton
                        onClick={() => handleGetDealClick(deal)}
                        aria-label={`Show code for ${deal.title}`}
                        couponCode={deal.couponCode}
                        mobile
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleGetDealClick(deal)}
                        className="inline-flex h-10 w-full cursor-pointer items-center justify-center rounded-full bg-purple-600 px-4 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-purple-700 whitespace-nowrap"
                      >
                        Get Deal
                      </button>
                    )}
                    <p className="text-[11px] text-center bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                      Used {usedTimes} times
                    </p>
                  </div>
                </div>
              );
              })
            ) : (
              <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200 text-center">
                <p className="text-sm text-gray-500">No coupons available for this event&apos;s store.</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Latest Events Section */}
      {latestEvents && latestEvents.length > 0 && (
        <section className="max-w-7xl w-full px-4 py-8 bg-white rounded-xl shadow border border-gray-100 mt-6 text-center">
          <h2 className="text-3xl font-bold text-purple-800 mb-8">The Latest Events</h2>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 max-w-6xl mx-auto justify-items-center">
            {latestEvents.map((eventItem, index) => {
              const formattedDate = eventItem.date
                ? new Date(eventItem.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                : "";

              return (
                <div
                  key={eventItem._id}
                  className="flex flex-col items-center w-full max-w-[301px] lg:ml-12"
                >
                  {/* Image */}
                  <div className="w-full h-[160px] bg-gray-200 rounded-[12px] overflow-hidden flex items-center justify-center border border-gray-100">
                    {eventItem.image ? (
                      <Image
                        src={`${domain}${eventItem.image}`}
                        alt={eventItem.title || "Event image"}
                        width={301}
                        height={160}
                        priority={index < 4}
                        loading={index < 4 ? "eager" : "lazy"}
                        className="-mt-6 w-full h-full object-contain bg-white p-2 transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm flex items-center justify-center h-full">
                        No Image
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div
                    className="w-full min-h-[148px] bg-white rounded-[12px] shadow-md -mt-6 z-10 flex flex-col justify-between p-4 text-left"
                    style={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                  >
                    <p className="text-xs text-gray-500">{formattedDate}</p>
                    <h3 className="text-sm font-semibold line-clamp-2">{eventItem.title}</h3>
                    <a
                      href={`/events/${eventItem.slug}`}
                      className="text-purple-700 font-semibold hover:underline flex items-center text-sm"
                    >
                      Read More... <span className="ml-1 text-purple-700">→</span>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Blogs Section */}
      <BlogSection blogs={blogs} />

      {/* Newsletter Section */}
      <section className="max-w-7xl w-full px-4 py-8 bg-white rounded-xl shadow border border-gray-100 text-center">
        <h2 className="text-xl font-bold mb-2">Join the Savings Revolution</h2>
        <p className="text-gray-600 mb-4">
          Get exclusive access to the best deals, early notifications of sales, and personalized coupon recommendations.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData();
            formData.set("email", email);
            startTransition(() => {
              dispatch(formData);
            });
          }}
          className="flex flex-col sm:flex-row justify-center items-center gap-2"
        >
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="px-3 py-2 w-full sm:w-auto border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <Button
            type="submit"
            className="bg-purple-700 text-white px-4 py-2 rounded-md hover:bg-purple-800 transition shadow-sm w-full sm:w-auto"
            disabled={isPending}
          >
            {isPending ? "Subscribing..." : "Subscribe"}
          </Button>
        </form>

        {errorFor("email") && <p className="text-red-500 mt-2">{errorFor("email")}</p>}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4 sm:mx-auto animate-fade-in">
            <DialogHeader className="relative">
              <DialogTitle className="text-lg sm:text-xl">Subscription Status</DialogTitle>
            </DialogHeader>
            <p className="py-2 text-sm sm:text-base">{dialogMessage}</p>
            <DialogFooter>
              <Button
                className="bg-purple-700 text-white px-6 py-2 rounded-md hover:bg-purple-800 transition shadow-sm"
                onClick={() => setDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>

      {/* Coupon Modal */}
      <CouponModal
        storeName={selectedDeal?.store?.name}
        storeSlug={selectedDeal?.store?.slug}
        storeImageUrl={selectedDeal?.store?.image}
        title={selectedDeal?.title}
        discount={selectedDeal?.discount}
        code={selectedDeal?.couponCode}
        redeemUrl={selectedDeal?.couponUrl}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default EventClient;
